import React from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Clock, Lock } from "lucide-react";
import useUsage from "@/hooks/useUsage";

function daysLeft(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 3600 * 1000)));
}

export default function UsageBadge() {
  const usage = useUsage();
  const navigate = useNavigate();
  if (usage.loading) return null;

  const isFree = usage.tier === "free";
  const expired = isFree && (usage.trial_expired || usage.remaining === 0);
  const dLeft = isFree ? daysLeft(usage.trial_expires_at) : null;
  const nearLimit = isFree && !expired && (usage.remaining <= 1 || (dLeft !== null && dLeft <= 2));
  const pct = usage.limit > 0 ? Math.min(100, (usage.used / usage.limit) * 100) : 0;

  const tone = expired
    ? "bg-red-50 border-red-200"
    : nearLimit
      ? "bg-amber-50 border-amber-200"
      : "bg-blue-50/60 border-blue-100";

  return (
    <button
      onClick={() => navigate("/pricing")}
      data-testid="usage-badge"
      className={`w-full text-left rounded-2xl p-3 border transition ${tone}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          {expired ? <Lock className="w-4 h-4 text-red-500" />
            : <Sparkles className={`w-4 h-4 ${nearLimit ? "text-amber-500" : "text-blue-600"}`} />}
          <span data-testid="usage-count">{usage.used} von {usage.limit} Dokumenten</span>
          {isFree && <span className="text-xs text-slate-500 font-medium">· Testphase</span>}
          {!isFree && <span className="text-xs text-slate-500 font-medium">· diesen Monat</span>}
        </div>
        <span className={`text-xs font-semibold uppercase tracking-wider ${expired ? "text-red-700" : nearLimit ? "text-amber-700" : "text-blue-700"}`} data-testid="usage-tier">
          {usage.tier === "free" ? "Kostenlos" : usage.tier === "plus" ? "Plus" : "Premium"}
        </span>
      </div>

      <div className="mt-2 h-1.5 rounded-full bg-white/70 overflow-hidden">
        <div
          className={`h-full ${expired ? "bg-red-500" : nearLimit ? "bg-amber-500" : "bg-blue-600"}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {isFree && !expired && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-600" data-testid="trial-remaining">
          <Clock className="w-3.5 h-3.5" />
          {dLeft === 0 ? "Letzter Tag der Testphase" : dLeft === 1 ? "Noch 1 Tag" : `Noch ${dLeft} Tage`}
          {" "}· dann Abo nötig
        </div>
      )}

      {expired && (
        <div className="mt-2 text-xs text-red-800 font-semibold" data-testid="usage-expired">
          Testphase beendet. Jetzt Plan wählen →
        </div>
      )}

      {isFree && !expired && nearLimit && (
        <div className="mt-2 text-xs text-amber-800" data-testid="usage-near-limit">
          {usage.remaining === 0 ? "Testlimit erreicht. Jetzt upgraden →"
            : usage.remaining === 1 ? "Noch 1 Dokument übrig. Upgraden →"
            : "Testphase endet bald. Upgraden →"}
        </div>
      )}
    </button>
  );
}
